import { Bar, Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { ctrSeries, dailySeries, revenueSeries, summarizeAdAnalytics } from "../../util/adAnalytics";
import "./AdsSponsorship.css";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
);

const themeColor = () => {
    if (typeof document === "undefined") {
        return "#3d8bfd";
    }
    return getComputedStyle(document.documentElement).getPropertyValue("--hoverColorBold").trim() || "#3d8bfd";
};

const money = (amount, currency) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD", maximumFractionDigits: 0 }).format(amount || 0);

const percent = (ctr) => `${(Number(ctr || 0) * 100).toFixed(1)}%`;

const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "top" } },
    scales: { y: { beginAtZero: true } },
};

const AdsAnalytics = ({ daily = {}, ctr = {}, revenue = {} }) => {
    const summary = summarizeAdAnalytics({ daily, revenue });
    const views = dailySeries(daily);
    const taps = ctrSeries(ctr);
    const collected = revenueSeries(revenue);
    const color = themeColor();

    return (
        <div className="ads-analytics" data-testid="ads-analytics-panel">
            <div>
                <h2>Analytics</h2>
                <p className="ads-card-lead">How ads performed in the app, and what sponsorship collected.</p>
            </div>
            <div className="ads-metrics">
                <article className="ads-metric">
                    <p>Views</p>
                    <strong>{summary.impressions.toLocaleString()}</strong>
                </article>
                <article className="ads-metric">
                    <p>Taps</p>
                    <strong>{summary.clicks.toLocaleString()}</strong>
                </article>
                <article className="ads-metric">
                    <p>Tap rate</p>
                    <strong>{percent(summary.ctr)}</strong>
                </article>
                <article className="ads-metric">
                    <p>Sponsorship collected</p>
                    <strong>{money(summary.revenue, summary.currency)}</strong>
                </article>
            </div>
            {!summary.hasActivity && (
                <p className="ads-empty-copy" data-testid="ads-analytics-empty">
                    No ad activity yet. Charts fill in after the app shows ads to members.
                </p>
            )}
            <div className="ads-chart-card">
                <h3>Views and taps</h3>
                {views.labels.length === 0 ? (
                    <p className="ads-empty-copy">No daily numbers yet.</p>
                ) : (
                    <div className="ads-chart" data-testid="ads-daily-chart">
                        <Line
                            data={{
                                labels: views.labels,
                                datasets: [
                                    { label: "Views", data: views.impressions, borderColor: color, backgroundColor: color, fill: false, tension: 0.3 },
                                    { label: "Taps", data: views.clicks, borderColor: "#102033", backgroundColor: "#102033", fill: false, tension: 0.3 },
                                ],
                            }}
                            options={lineOptions}
                        />
                    </div>
                )}
            </div>
            <div className="ads-chart-grid">
                <div className="ads-chart-card">
                    <h3>Tap rate by placement</h3>
                    {taps.labels.length === 0 ? (
                        <p className="ads-empty-copy">No placement numbers yet.</p>
                    ) : (
                        <div className="ads-chart" data-testid="ads-ctr-chart">
                            <Bar
                                data={{
                                    labels: taps.labels,
                                    datasets: [{ label: "Tap rate %", data: taps.values, backgroundColor: color }],
                                }}
                                options={lineOptions}
                            />
                        </div>
                    )}
                </div>
                <div className="ads-chart-card">
                    <h3>Sponsorship collected</h3>
                    {collected.labels.length === 0 ? (
                        <p className="ads-empty-copy">No collected amounts yet.</p>
                    ) : (
                        <div className="ads-chart" data-testid="ads-revenue-chart">
                            <Bar
                                data={{
                                    labels: collected.labels,
                                    datasets: [{ label: summary.currency || "USD", data: collected.values, backgroundColor: color }],
                                }}
                                options={lineOptions}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdsAnalytics;
